import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { FormControl, FormGroup } from '@angular/forms';
import { UserBalanceDto } from 'src/app/interfaces/user-balance.dto';
import { LoaderService } from 'src/app/services/loader.service';
import { UserBalanceService } from 'src/app/services/user-balance.service';
import Swal from 'sweetalert2';
import { BalanceListComponent } from './balance-list/balance-list.component';
import { SharedModule } from '../shared/shared.module';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-balance-resume',
  templateUrl: './balance-resume.component.html',
  styleUrls: ['./balance-resume.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, BalanceListComponent]
})
export class BalanceResumeComponent implements OnInit {
  searchForm: FormGroup = this.createFormSearch();
  balances: UserBalanceDto[] = [];
  loading: boolean = false;
  showCeros: boolean = false;
  resume: { page: number; range: number; total: number } | undefined =
    undefined;

  currentPage: number = 1;
  totalPages: number = 1;

  constructor(
    private readonly userBalanceService: UserBalanceService,
    private readonly loaderService: LoaderService
  ) {}

  ngOnInit() {
    this.loadBalances();
  }

  createFormSearch() {
    return new FormGroup({
      order: new FormControl(0),
      query: new FormControl(null),
      startDate: new FormControl(null),
      endDate: new FormControl(null),
    });
  }

  getQuery() {
    return this.searchForm.get('query')?.value || '';
  }

  getOrder() {
    return this.searchForm.get('order')?.value || '';
  }

  getStartDate(): number {
    const date = this.searchForm.get('startDate')?.value;
    if (!date) return 0;
    // Establecer inicio del día (00:00:00)
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay.getTime();
  }

  getEndDate(): number {
    const date = this.searchForm.get('endDate')?.value;
    if (!date) return 0;
    // Establecer final del día (23:59:59.999)
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime();
  }

  loadBalances() {
    this.loading = true;
    this.loaderService.showSpinner('Cargando balances');
    this.userBalanceService
      .getBalances(
        this.currentPage,
        10,
        this.getQuery(),
        this.getOrder(),
        this.showCeros,
        this.getStartDate(),
        this.getEndDate()
      )
      .then((data) => {
        if (!data || data.rows.length === 0) {
          this.balances = [];
          this.resume = { page: 0, range: 0, total: 0 };
          this.loading = false;
          this.loaderService.hideSpinner();
          Swal.fire(
            'No hay datos',
            'No se han encontrado balances con los criterios indicados o ha ocurrido un error al cargarlos.',
            'info'
          );
          return;
        }

        this.balances = data.rows.map(
          (b: { email: string; amount: number; lastUpdate: number; error?: string }) =>
            new UserBalanceDto(b.email, b.amount, b.lastUpdate, b.error || '')
        );
        this.totalPages = data.pages;
        this.resume = data.balance;
        this.loading = false;
        this.loaderService.hideSpinner();
      })
      .catch((error) => {
        this.loading = false;
        this.loaderService.hideSpinner();
        console.error('Error loading balances:', error);
        Swal.fire('Error', 'Ha ocurrido un error al cargar los balances.', 'error');
      });
  }

  handleSearch() {
    this.currentPage = 1;
    this.loadBalances();
  }

  handleShowCeros() {
    this.showCeros = !this.showCeros;
    this.currentPage = 1;
    this.loadBalances();
  }

  handleOrderChange() {
    this.currentPage = 1;
    this.loadBalances();
  }

  getCheckText(error: string = ''): string {
    if (error === 'no existe') {
      return 'Usuario desconocido';
    } else if (error === 'error general') {
      return 'Error al obtener el balance';
    } else return '';
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadBalances();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadBalances();
    }
  }

  goToPage(i: number) {
    this.currentPage = i + 1;
    this.loadBalances();
  }

  downloadExcel() {
    this.loaderService.showSpinner('Generando Excel...');
    
    // Obtener todos los datos sin paginación para el Excel
    this.userBalanceService
      .getBalances(
        1,
        10000, // Número grande para obtener todos los registros
        this.getQuery(),
        this.getOrder(),
        this.showCeros,
        this.getStartDate(),
        this.getEndDate()
      )
      .then((data) => {
        if (!data || !data.rows || data.rows.length === 0) {
          this.loaderService.hideSpinner();
          Swal.fire('Sin datos', 'No hay datos para exportar.', 'info');
          return;
        }

        // Preparar datos para Excel
        const excelData = data.rows.map((balance: any) => ({
          Email: balance.email,
          Balance: balance.error ? 'Sin balance' : balance.amount.toFixed(2) + ' €',
          'Última actualización': balance.error 
            ? 'N/A' 
            : balance.lastUpdate 
              ? new Date(balance.lastUpdate).toLocaleDateString('es-ES') 
              : 'N/A',
          Estado: balance.error || 'OK'
        }));

        // Añadir fila de resumen
        if (data.balance) {
          excelData.push({
            Email: 'RESUMEN',
            Balance: '',
            'Última actualización': '',
            Estado: ''
          });
          excelData.push({
            Email: 'Total en página',
            Balance: data.balance.page.toFixed(2) + ' €',
            'Última actualización': '',
            Estado: ''
          });
          excelData.push({
            Email: 'Total en rango',
            Balance: data.balance.range.toFixed(2) + ' €',
            'Última actualización': '',
            Estado: ''
          });
          excelData.push({
            Email: 'Total en sistema',
            Balance: data.balance.total.toFixed(2) + ' €',
            'Última actualización': '',
            Estado: ''
          });
        }

        // Crear workbook y worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Saldos de Carteras');

        // Generar nombre de archivo con fecha
        const dateStr = new Date().toISOString().split('T')[0];
        const fileName = `saldos_carteras_${dateStr}.xlsx`;

        // Descargar archivo
        XLSX.writeFile(workbook, fileName);
        this.loaderService.hideSpinner();
        Swal.fire('Éxito', 'El archivo Excel se ha descargado correctamente.', 'success');
      })
      .catch((error) => {
        this.loaderService.hideSpinner();
        console.error('Error generating Excel:', error);
        Swal.fire('Error', 'Ha ocurrido un error al generar el archivo Excel.', 'error');
      });
  }

  clearDateFilters() {
    this.searchForm.patchValue({
      startDate: null,
      endDate: null
    });
    this.currentPage = 1;
    this.loadBalances();
  }
}
